import { z } from 'zod';

/** ULIDs: 26 chars, Crockford base32, lexicographically sortable by creation time. */
export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'expected a ULID');
/** Cognito `sub` — a UUID. Kept separate from ULID so the difference stays visible. */
export const userIdSchema = z.string().uuid();

/** Integer cents. Floats and strings are rejected at the edge so money can never drift. */
export const centsSchema = z.number().int().min(0).max(1_000_000_000);
export const currencySchema = z.string().length(3).toUpperCase().default('USD');

export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const isoDateTimeSchema = z.string().datetime();

export const shortText = z.string().trim().min(1).max(200);
export const optionalText = z.string().trim().max(200).optional();
export const notesText = z.string().trim().max(5000).optional();
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const urlSchema = z.string().trim().url().max(2048);
export const tagsSchema = z.array(z.string().trim().min(1).max(40)).max(25).default([]);

/** A URL-safe public slug: lowercase letters, numbers and single dashes. */
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'letters, numbers and dashes only');

/** Written on every item by the repository layer. */
export const auditFields = {
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  updatedBy: z.string(),
};

/** Optimistic concurrency: a PATCH echoes the `updatedAt` it last saw. */
export const ifUnmodifiedSince = z.object({ updatedAt: isoDateTimeSchema });

export const listQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  cursor: z.string().max(4096).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export interface ListResponse<T> {
  items: T[];
  nextCursor?: string;
}
